import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useRechargeStore} from '../recharge.store';
import './recharge-tab.css';
import {
  getActivityPayList,
  filterActivityPayListForUseType,
} from './recharge-tab.service';
import {RechargeWebActivityPayTabs} from '@fe-progressive/package-progressive/recharge-web';
import {normalizeProgressivePackageLocale} from '@fe-progressive/package-progressive/locale';

const CORNER_RIBBON_BG = require('@/assets/imgs/recharge/tag.webp') as string;

// Props接口（可选，用于外部控制）
export interface RechargeTabProps {
  activityListUseType?: number;
  useType?: number;
  onTabChange?: (tabId: number) => void;
  loading?: boolean;
  /** 覆盖 `useTranslation().i18n.language`，供包内默认文案 */
  locale?: string;
  /** 选中角标 SVG 对号描边；默认白 */
  selectionBadgeCheckStroke?: string;
}

const RechargeTab: React.FC<RechargeTabProps> = ({
  activityListUseType,
  useType,
  onTabChange,
  loading: externalLoading,
  locale: localeProp,
  selectionBadgeCheckStroke,
}) => {
  const {i18n} = useTranslation();
  const activityPayTabsLocale = useMemo(
    () => normalizeProgressivePackageLocale(localeProp ?? i18n.language),
    [localeProp, i18n.language],
  );
  const resolvedUseType = activityListUseType ?? useType;
  const {
    tabs,
    activeTabId,
    loading: storeLoading,
    setActiveTabId,
    setTabs,
  } = useRechargeStore();

  const [tabFetchLoading, setTabFetchLoading] = useState(true);

  const loading =
    (externalLoading !== undefined ? externalLoading : storeLoading) ||
    tabFetchLoading;

  const handleTabPress = useCallback(
    (tabId: number) => {
      setActiveTabId(tabId);
      const {updateTabActiveStatus} = useRechargeStore.getState();
      updateTabActiveStatus(tabId, true);
      onTabChange?.(tabId);
    },
    [setActiveTabId, onTabChange],
  );

  useEffect(() => {
    let cancelled = false;
    setTabFetchLoading(true);
    (async () => {
      try {
        const res = await getActivityPayList(resolvedUseType);
        if (cancelled) {
          return;
        }
        setTabs(filterActivityPayListForUseType(res, resolvedUseType ?? 1));
      } finally {
        if (!cancelled) {
          setTabFetchLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedUseType, setTabs]);

  if (!loading && tabs.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <span className="recharge-tab-loading text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <RechargeWebActivityPayTabs
      tabs={tabs}
      activeTabId={activeTabId ?? tabs[0]!.id}
      onSelectTab={handleTabPress}
      locale={activityPayTabsLocale}
      cornerRibbonBackgroundUrl={CORNER_RIBBON_BG}
      selectionBadgeCheckStroke={selectionBadgeCheckStroke}
    />
  );
};

export default RechargeTab;
